/**
 * Ambience: the wind around you, the rain on the skin, and the birds that stop when you start up.
 *
 * ```
 *   pink ─► lowpass(600) ─► [AM by a slow gust LFO] ─► wind bed gain(← surface wind, ducked by IAS)
 *   white ─► highpass(1400) ─► rain hiss gain(← precipitation × impact speed)
 *   white ─► bandpass(280) ──► rain drumming gain
 *   baked bird calls ─► fired on a randomised countdown while the aeroplane is parked and quiet
 * ```
 *
 * ## The duck is the point
 *
 * The wind bed and the airframe's wind layer are the *same physical air* heard two different
 * ways: standing on the ramp you hear it moving around you, and at 120 kt you hear it moving past
 * you. Running both at once is not twice as windy, it is mud. So the bed is ducked out by
 * indicated airspeed and fully gone by 50 kt, at which point the airframe layer has taken over —
 * one hand-off, no overlap, and the transition happens during the take-off roll where there is
 * plenty else going on to cover it.
 *
 * ## Rain, and what Overcast is allowed to mean
 *
 * The last milestone's whole point was that a METAR sky condition means what it says, and OVC
 * does not mean rain. So this does not infer rain from cover: `WeatherState` carries a
 * `precipitation` field, and the *preset* decides what to put in it — which for the shipped
 * Overcast is a light 0.35 and for everything else is zero. That keeps the sound honest, and it
 * leaves precipitation as an independent axis for a future weather model to drive.
 *
 * Two bands, because rain on an aeroplane is two sounds: hiss on the windscreen and drumming on
 * the skin above your head. Both scale with how fast you are driving into the drops.
 *
 * ## Birds
 *
 * A small thing that does a lot of work. They are on only when the aeroplane is parked with the
 * engine stopped, which makes the silence before a start feel like a *place* rather than like the
 * audio not having loaded yet — and the first thing the starter does is take them away.
 *
 * `updateFrame(alpha)`: {@link AmbientVoice.updateFrame}, once per rendered frame, with the real
 * frame delta so the bird countdown runs in wall-clock time.
 */

import { Rng } from '../../core/rng.js';
import type { AudioBank } from '../bank.js';
import { ambientWindGain, birdsongGain, rainGain } from '../model.js';
import { setTarget } from '../param.js';
import type { AircraftAudioState, EnvironmentAudioState } from '../state.js';
import { OneShotPool, bandpass, disconnectAll, gainNode, highpass, loopingSource, lowpass } from './parts.js';

/** Shortest and longest gap between bird calls [s]. */
const BIRD_GAP_MIN_S = 1.6;
const BIRD_GAP_MAX_S = 6.5;

export class AmbientVoice {
  private readonly context: AudioContext;
  private readonly bank: AudioBank;
  private readonly rng: Rng;

  private readonly windSource: AudioBufferSourceNode;
  private readonly windLow: BiquadFilterNode;
  private readonly windAm: GainNode;
  private readonly gustOscillator: OscillatorNode;
  private readonly gustDepth: GainNode;
  private readonly windLevel: GainNode;

  private readonly rainSource: AudioBufferSourceNode;
  private readonly rainHiss: BiquadFilterNode;
  private readonly rainHissLevel: GainNode;
  private readonly rainDrum: BiquadFilterNode;
  private readonly rainDrumLevel: GainNode;

  private readonly birdLevel: GainNode;
  private readonly birdShots: OneShotPool;
  private birdCountdownS = 2;

  private readonly output: GainNode;

  constructor(context: AudioContext, bank: AudioBank, destination: AudioNode, rng: Rng) {
    this.context = context;
    this.bank = bank;
    this.rng = rng;

    this.output = gainNode(context, 1);
    this.output.connect(destination);

    // --- the wind you stand in ---------------------------------------------------------------
    this.windLevel = gainNode(context, 0);
    this.windLevel.connect(this.output);
    this.windAm = gainNode(context, 0.75);
    this.windAm.connect(this.windLevel);
    this.windLow = lowpass(context, 600, 0.8);
    this.windLow.connect(this.windAm);
    // Pink rather than white: outdoor wind genuinely has a 1/f spectrum, and white noise through
    // a lowpass sounds like a hiss with the top taken off rather than like weather.
    this.windSource = loopingSource(context, bank.pink, bank.pinkLoopEndS, 1, 0.5);
    this.windSource.connect(this.windLow);

    this.gustOscillator = context.createOscillator();
    this.gustOscillator.type = 'sine';
    // Gusts on a scale of ten seconds. Slower than it feels like it should be — a faster
    // modulation reads as a effect rather than as wind.
    this.gustOscillator.frequency.value = 0.11;
    this.gustDepth = gainNode(context, 0.25);
    this.gustOscillator.connect(this.gustDepth);
    this.gustDepth.connect(this.windAm.gain);
    this.gustOscillator.start(0);

    // --- rain ------------------------------------------------------------------------------
    this.rainHissLevel = gainNode(context, 0);
    this.rainHissLevel.connect(this.output);
    this.rainHiss = highpass(context, 1_400);
    this.rainHiss.connect(this.rainHissLevel);
    this.rainDrumLevel = gainNode(context, 0);
    this.rainDrumLevel.connect(this.output);
    this.rainDrum = bandpass(context, 280, 0.9);
    this.rainDrum.connect(this.rainDrumLevel);
    this.rainSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 1.02, 1.2);
    this.rainSource.connect(this.rainHiss);
    this.rainSource.connect(this.rainDrum);

    // --- birds ------------------------------------------------------------------------------
    this.birdLevel = gainNode(context, 0);
    this.birdLevel.connect(this.output);
    this.birdShots = new OneShotPool(context, this.birdLevel, rng, 4);
  }

  updateFrame(
    aircraft: AircraftAudioState,
    environment: EnvironmentAudioState,
    frameDtS: number,
  ): void {
    const now = this.context.currentTime;
    const ias = Math.max(0, aircraft.indicatedAirspeedMps);

    setTarget(
      this.windLevel.gain,
      ambientWindGain(environment.surfaceWindMps, ias) * 0.45,
      0.2,
      now,
    );

    const rain = rainGain(environment.precipitation, ias);
    setTarget(this.rainHissLevel.gain, rain * 0.22, 0.25, now);
    setTarget(this.rainDrumLevel.gain, rain * 0.14, 0.25, now);

    // --- birds ---------------------------------------------------------------------------------
    const birds = birdsongGain(
      aircraft.onGround,
      aircraft.engineRunning,
      aircraft.rpm,
      aircraft.groundSpeedMps,
    );
    setTarget(this.birdLevel.gain, birds * 0.32, 0.6, now);
    /*
     * The countdown runs only while the gate is open, so starting the engine does not merely mute
     * the birds — it stops the schedule, and shutting down again begins a fresh silence rather
     * than dumping the calls that would have played during the flight.
     */
    if (birds > 0.05) {
      this.birdCountdownS -= frameDtS;
      if (this.birdCountdownS <= 0) {
        const call = this.bank.birds[this.rng.int(0, this.bank.birds.length - 1)];
        this.birdShots.play(call, this.rng.range(0.5, 1), this.rng.range(0.88, 1.15));
        this.birdCountdownS = this.rng.range(BIRD_GAP_MIN_S, BIRD_GAP_MAX_S);
      }
    } else {
      this.birdCountdownS = this.rng.range(0.5, 2.5);
    }
  }

  dispose(): void {
    this.gustOscillator.stop();
    this.windSource.stop();
    this.rainSource.stop();
    disconnectAll([
      this.windSource,
      this.windLow,
      this.windAm,
      this.gustOscillator,
      this.gustDepth,
      this.windLevel,
      this.rainSource,
      this.rainHiss,
      this.rainHissLevel,
      this.rainDrum,
      this.rainDrumLevel,
      this.birdLevel,
      this.output,
    ]);
  }
}
